import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Height to keep clear at the bottom of the freeze divider when the platform
// draws overlay scrollbars (macOS): the bar is ~7px plus its margin.
const OVERLAY_SCROLLBAR = 11;

/**
 * Excel-style freeze panes for a horizontally scrolling table.
 *
 * The browser decides <td> widths under `table-layout: auto`, so the second
 * frozen column's `left` cannot be hardcoded — it has to be measured off the
 * first one, or the columns overlap. Returns the measured offsets plus whether
 * the container is scrolled off its left edge, which is when the divider line
 * should appear.
 *
 * @param {number} count how many leading columns are frozen
 * @param {*} key re-measure when this changes (e.g. the active tab), since the
 *   table unmounts on tab switch and the refs would otherwise stay stale
 */
export function useFrozenColumns(count, key) {
  const refs = useRef([]);
  const [widths, setWidths] = useState(() => new Array(count).fill(0));
  const [scrolled, setScrolled] = useState(false);
  const [scrollbar, setScrollbar] = useState(0);

  useLayoutEffect(() => {
    const nodes = refs.current.slice(0, count).filter(Boolean);
    if (nodes.length === 0) return undefined;
    const measure = () => {
      // getBoundingClientRect, not offsetWidth: the latter rounds to an integer
      // and a column that really is 50.6px wide would report 51, leaving a
      // sub-pixel gap between this frozen cell and the next one that scrolling
      // text shows through.
      const next = nodes.map((n) => n.getBoundingClientRect().width || 0);
      // Guard the update: this effect re-runs on remount and a fresh array
      // every time would loop forever.
      setWidths((prev) =>
        prev.length === next.length && prev.every((w, i) => w === next[i]) ? prev : next
      );
      // How much of the scroll container's height the horizontal scrollbar
      // eats, so the divider can stop short of it. Classic scrollbars (Windows,
      // Linux) take real layout space and report here; macOS overlay
      // scrollbars report 0 and are handled by the floor in `dividerBottom`.
      // The Catalyst <Table> owns the scroll container, so it is found by class
      // rather than by a ref the caller would have to thread through.
      const scroller = nodes[0].closest('.overflow-x-auto');
      if (scroller) {
        const h = Math.max(0, scroller.offsetHeight - scroller.clientHeight);
        setScrollbar((prev) => (prev === h ? prev : h));
      }
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    nodes.forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [count, key]);

  const setRef = useCallback((i) => (node) => { refs.current[i] = node; }, []);

  const onScroll = useCallback((e) => {
    setScrolled(e.currentTarget.scrollLeft > 0);
  }, []);

  // Left offset of frozen column i = the sum of the widths before it, pulled
  // back a pixel so consecutive frozen cells OVERLAP instead of risking a
  // hairline gap. Two separately-composited sticky layers that merely touch can
  // still show a seam, and the cell backgrounds are opaque, so a 1px overlap of
  // trailing padding is invisible while a 1px gap leaks scrolling text.
  const offsets = widths.map((_, i) => {
    if (i === 0) return 0;
    const sum = widths.slice(0, i).reduce((a, b) => a + b, 0);
    return Math.max(0, Math.round(sum) - 1);
  });
  const sum = widths.reduce((a, b) => a + b, 0);
  // `total` rounds UP so anything parked after the frozen block (a pinned
  // panel) clears it completely. `dividerLeft` rounds DOWN and pulls back a
  // pixel so the divider overlaps the frozen block's trailing edge instead of
  // abutting it — abutting leaves a sub-pixel gap that scrolling text shows
  // through, and the divider is opaque so the overlap is invisible.
  const total = Math.ceil(sum);
  const dividerLeft = Math.max(0, Math.floor(sum) - 1);
  // The divider is only ever shown while the table is scrolled sideways, which
  // is exactly when a horizontal scrollbar is on screen — so it always has to
  // clear one. The floor covers macOS overlay scrollbars, which take no layout
  // space but still paint over the bottom of the content.
  const dividerBottom = Math.max(scrollbar, OVERLAY_SCROLLBAR);

  return { setRef, offsets, total, dividerLeft, dividerBottom, scrolled, onScroll };
}

/**
 * Classes for a frozen body cell. Sticky cells need an OPAQUE background: the
 * row tint sits on the <tr> and scrolling content would show straight through a
 * transparent <td>. These hexes are the flattened equivalents of the row tints
 * composited over white, and must be kept in step with the row classes they
 * mirror:
 *   bg-beige-suave/15   -> #F7F8F2   (zebra stripe)
 *   bg-azul-surelia/5   -> #F4F6F7   (hover)
 *   bg-cian-digital/25  -> #D4EDF2   (return-from-dialog flash)
 * `highlight` must be threaded through, or the flash tints the row everywhere
 * except its frozen columns.
 */
export const frozenCellClass = (index, highlight = false) =>
  highlight
    ? 'sticky z-10 bg-[#D4EDF2] transition-colors duration-700'
    : `sticky z-10 transition-colors duration-700 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F7F8F2]'} group-hover:bg-[#F4F6F7]`;

/**
 * Classes for a frozen header cell.
 *
 * The <thead> this sits in MUST carry a flat `bg-azul-surelia`, never the
 * `from-azul-surelia to-azul-surelia/95` gradient the non-frozen tables use. A
 * sticky cell is composited on its own layer and snaps to whole device pixels,
 * while the thead behind it is painted in normal flow at whatever fractional
 * offset the table landed on (the Catalyst kit wraps the table in an
 * `inline-block align-middle`, which routinely lands on a half pixel). Against
 * a gradient that misregistration reads two ways at once: a hairline of the
 * lighter gradient shows ABOVE the frozen cell, so the header looks notched,
 * and the solid cell drifts out of tone with the gradient sliding underneath it
 * as the table scrolls. Both disappear when the two layers are the same flat
 * colour.
 */
export const frozenHeadClass = 'sticky z-20 bg-azul-surelia';

// NOTE: the freeze divider is deliberately NOT a border on the frozen cells.
// A per-cell border scrolls with the cell it belongs to; callers render a single
// absolutely-positioned rule at `left: dividerLeft`, anchored to a non-scrolling
// wrapper around the overflow container, so it stays pinned to the boundary.
// Callers pin it with `top-0` + `bottom: dividerBottom` — NOT `inset-y-0`, which
// runs the rule straight over the horizontal scrollbar.
